import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiErrToStr, resolveImg } from "@/lib/api";
import EntityCard from "@/components/EntityCard";
import { HtmlContent } from "@/components/HtmlContent";
import { Gallery } from "@/components/Gallery";
import { Reviews } from "@/components/Reviews";
import { useLang } from "@/context/LangContext";
import { useSeo } from "@/lib/useSeo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MapPin, Star, Clock, Mountain, Plane, Calendar as CalIcon, MessageCircle, Phone, ChevronRight, Home as HomeIcon, LogIn, LogOut, Check } from "lucide-react";
import { Link } from "react-router-dom";

const FALLBACK = "https://images.unsplash.com/photo-1571536802807-30451e3955d8?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function EntityDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/entities/${slug}`).then(r => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const ent = data?.entity;
  const jsonLd = useMemo(() => {
    if (!ent) return null;
    const isStay = ent.type === "hotel" || ent.type === "dharamshala";
    const ld = {
      "@context": "https://schema.org", "@type": isStay ? "LodgingBusiness" : (ent.type === "package" || ent.type === "yatra" ? "TouristTrip" : "TouristAttraction"),
      name: ent.name, image: resolveImg(ent.image), description: ent.short_desc,
      address: ent.address ? { "@type": "PostalAddress", streetAddress: ent.address, addressLocality: ent.city, addressRegion: ent.state, addressCountry: "IN" } : undefined,
    };
    if (isStay) { ld.checkinTime = ent.check_in_time; ld.checkoutTime = ent.check_out_time; ld.amenityFeature = (ent.facilities || []).map((f) => ({ "@type": "LocationFeatureSpecification", name: f })); }
    if (ent.price) ld.offers = { "@type": "Offer", price: ent.price, priceCurrency: "INR" };
    if (data.review_count) ld.aggregateRating = { "@type": "AggregateRating", ratingValue: data.review_avg, reviewCount: data.review_count };
    return ld;
  }, [ent, data]);
  useSeo({ title: ent ? (ent.seo_title || `${ent.name}${ent.city ? ", " + ent.city : ""}`) : undefined, description: ent ? (ent.seo_desc || ent.short_desc) : undefined, image: ent ? resolveImg(ent.image) : undefined, jsonLd });

  if (notFound) return <div className="max-w-3xl mx-auto p-12 text-center"><h1 className="font-display text-2xl text-indigo-deep">Not found</h1></div>;
  if (!data) return (
    <div className="max-w-6xl mx-auto p-4">
      <Skeleton className="h-80 rounded-xl mb-6" />
      <Skeleton className="h-8 w-1/2 mb-3" /><Skeleton className="h-4 w-3/4 mb-6" />
      <Skeleton className="h-40" />
    </div>
  );

  const e = data.entity;
  const isBookable = e.is_bookable && e.price;
  const isStay = e.type === "hotel" || e.type === "dharamshala";
  const gallery = [e.image, ...(e.gallery || [])].filter(Boolean);

  return (
    <div>
      {/* Hero */}
      <div className="relative h-72 md:h-96 overflow-hidden bg-indigo-deep">
        <img src={resolveImg(e.image) || FALLBACK} alt={e.name} onError={(ev) => (ev.currentTarget.src = FALLBACK)} className="w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-indigo-deep via-indigo-deep/40 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 max-w-7xl mx-auto px-4 pb-6 text-white">
          <div className="flex items-center gap-1 text-xs text-white/70 mb-2">
            <Link to="/" className="hover:text-gold"><HomeIcon className="w-3 h-3 inline" /></Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`/${e.type}s`} className="hover:text-gold capitalize">{e.type}s</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/90">{e.name}</span>
          </div>
          <span className="text-[10px] font-semibold tracking-widest uppercase bg-gold text-indigo-deep px-2 py-1 rounded-full">{e.type}</span>
          <h1 data-testid="entity-title" className="font-display text-3xl md:text-5xl font-bold mt-2 leading-tight">{e.name}</h1>
          {(e.city || e.state || e.address) && (
            <div className="mt-2 flex items-center gap-1 text-sm text-white/80">
              <MapPin className="w-4 h-4" /> {e.address || [e.city, e.state].filter(Boolean).join(", ")}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 grid lg:grid-cols-[1fr_360px] gap-8">
        <div>
          {/* Quick stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {e.altitude_m && <Stat icon={Mountain} label={t("altitude")} value={`${e.altitude_m} m`} />}
            {e.best_season && <Stat icon={CalIcon} label={t("best_time")} value={e.best_season} />}
            {e.darshan_timings && <Stat icon={Clock} label={t("timings")} value={e.darshan_timings} />}
            {e.nearest_airport && <Stat icon={Plane} label={t("nearest_airport")} value={e.nearest_airport} />}
            {e.duration_days && <Stat icon={Clock} label={t("duration")} value={`${e.duration_days} ${t("days")}`} />}
            {e.check_in_time && <Stat icon={LogIn} label={t("check_in")} value={e.check_in_time} />}
            {e.check_out_time && <Stat icon={LogOut} label={t("check_out")} value={e.check_out_time} />}
            {(data.review_avg || e.rating) && <Stat icon={Star} label={t("rating")} value={`${data.review_avg || e.rating} / 5`} />}
          </div>

          <div className="prose max-w-none">
            <h2 className="font-display text-2xl text-indigo-deep">{t("about")} {e.name}</h2>
            {/<[a-z][\s\S]*>/i.test(e.description || "") ? <HtmlContent html={e.description} /> : <p className="text-indigo-deep/80 leading-relaxed">{e.description || e.short_desc}</p>}
          </div>

          {gallery.length > 1 && (
            <div className="mt-6">
              <h3 className="font-display text-xl text-indigo-deep mb-3">{t("gallery")}</h3>
              <Gallery images={gallery} title={e.name} />
            </div>
          )}

          {e.address && (
            <div className="mt-6 flex items-start gap-3 bg-white rounded-lg p-4 border border-gold/15" data-testid="entity-address">
              <MapPin className="w-5 h-5 text-gold-dark shrink-0 mt-0.5" />
              <div><div className="text-[11px] uppercase tracking-wider text-indigo-deep/50">{t("address")}</div><div className="text-sm text-indigo-deep">{e.address}</div></div>
            </div>
          )}

          {e.facilities?.length > 0 && (
            <div className="mt-6" data-testid="entity-facilities">
              <h3 className="font-display text-xl text-indigo-deep mb-3">{t("facilities")}</h3>
              <ul className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                {e.facilities.map((f) => <li key={f} className="flex items-center gap-2 text-sm bg-white rounded-lg px-3 py-2 border border-gold/15"><Check className="w-4 h-4 text-emerald-600" />{f}</li>)}
              </ul>
            </div>
          )}

          {e.highlights?.length > 0 && (
            <div className="mt-6">
              <h3 className="font-display text-xl text-indigo-deep mb-3">{t("highlights")}</h3>
              <ul className="grid sm:grid-cols-2 gap-2">
                {e.highlights.map((h) => <li key={h} className="flex items-start gap-2 text-sm"><span className="text-gold mt-1">✦</span>{h}</li>)}
              </ul>
            </div>
          )}

          {e.inclusions?.length > 0 && (
            <div className="mt-6">
              <h3 className="font-display text-xl text-indigo-deep mb-3">{t("included")}</h3>
              <div className="flex flex-wrap gap-2">
                {e.inclusions.map((h) => <span key={h} className="text-xs px-3 py-1.5 rounded-full bg-gold/15 text-gold-dark border border-gold/30">{h}</span>)}
              </div>
            </div>
          )}

          {isStay && e.policy && (
            <div className="mt-6" data-testid="entity-policy">
              <h3 className="font-display text-xl text-indigo-deep mb-3">{t("policy")}</h3>
              <div className="bg-white rounded-lg p-4 border border-gold/15 text-sm"><HtmlContent html={e.policy} /></div>
            </div>
          )}

          <Reviews entity={e} reviews={data.reviews || []} avg={data.review_avg} count={data.review_count || 0} />

          <div className="spiritual-divider my-10" />

          {data.related?.length > 0 && (
            <div>
              <h3 className="font-display text-2xl text-indigo-deep mb-4">{t("also_like")}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.related.slice(0, 3).map(r => <EntityCard key={r.id} e={r} compact />)}
              </div>
            </div>
          )}
        </div>

        {/* Sticky sidebar */}
        <aside className="hidden lg:block">
          <div className="sticky top-20 bg-white rounded-xl p-6 card-shadow border border-gold/20">
            {isBookable ? (
              <>
                <div className="text-xs text-indigo-deep/60 uppercase tracking-widest">{t("starting_from")}</div>
                <div className="font-display text-3xl font-bold text-saffron">₹{Number(e.price).toLocaleString("en-IN")}</div>
                <div className="text-xs text-indigo-deep/60 mb-4">{e.type === "package" || e.type === "yatra" ? t("per_person") : t("per_night")}</div>
                <Button data-testid="book-now-btn" onClick={() => nav(`/checkout/${e.slug}`)} className="w-full h-11 bg-gold text-indigo-deep hover:bg-gold-dark hover:text-white font-semibold">{t("book_now")}</Button>
              </>
            ) : (
              <>
                <div className="font-display text-xl text-indigo-deep mb-1">{t("custom_quote")}</div>
                <p className="text-sm text-indigo-deep/60 mb-4">{t("custom_quote_sub")}</p>
              </>
            )}
            <InquirySheet entity={e} triggerLabel={t("send_inquiry")} triggerClass="w-full h-11 mt-2 bg-indigo-deep text-white hover:bg-indigo" />
            <div className="mt-3 grid grid-cols-2 gap-2">
              <a href={`https://wa.me/919999999999?text=Hi%2C%20I'm%20interested%20in%20${encodeURIComponent(e.name)}`} target="_blank" rel="noreferrer">
                <Button data-testid="detail-whatsapp" variant="outline" className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50"><MessageCircle className="w-4 h-4 mr-1" />{t("whatsapp")}</Button>
              </a>
              <a href="tel:+919999999999">
                <Button data-testid="detail-call" variant="outline" className="w-full border-gold/40 text-indigo-deep hover:bg-gold/10"><Phone className="w-4 h-4 mr-1" />{t("call")}</Button>
              </a>
            </div>
          </div>
        </aside>
      </div>

      {/* Mobile sticky CTA */}
      <div className="lg:hidden fixed bottom-14 inset-x-0 z-30 bg-white border-t border-gold/20 p-3 flex gap-2" style={{ boxShadow: "0 -4px 25px rgba(16,20,42,0.12)" }}>
        {isBookable ? (
          <>
            <div className="flex-1">
              <div className="text-[10px] text-indigo-deep/60">{t("from")}</div>
              <div className="font-display font-bold text-saffron">₹{Number(e.price).toLocaleString("en-IN")}</div>
            </div>
            <Button data-testid="mobile-book-btn" onClick={() => nav(`/checkout/${e.slug}`)} className="flex-1 bg-gold text-indigo-deep font-semibold">{t("book_now")}</Button>
          </>
        ) : (
          <InquirySheet entity={e} triggerLabel={t("send_inquiry")} triggerClass="flex-1 h-11 bg-indigo-deep text-white" />
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-lg p-3 border border-gold/15">
      <Icon className="w-4 h-4 text-gold-dark mb-1" />
      <div className="text-[11px] uppercase tracking-wider text-indigo-deep/50">{label}</div>
      <div className="text-sm font-semibold text-indigo-deep">{value}</div>
    </div>
  );
}

export function InquirySheet({ entity, triggerClass, triggerLabel = "Send Inquiry" }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", travel_date: "", travelers: 2, message: "" });
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!f.name || !f.email || !f.phone) { toast.error("Name, email and phone are required"); return; }
    setSending(true);
    try {
      await api.post("/inquiries", { ...f, entity_id: entity.id, entity_name: entity.name });
      toast.success("Inquiry sent! Our team will reach out within 24 hours.");
      setOpen(false);
      setF({ name: "", email: "", phone: "", travel_date: "", travelers: 2, message: "" });
    } catch (err) { toast.error(apiErrToStr(err)); }
    finally { setSending(false); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button data-testid="open-inquiry-btn" className={triggerClass}>{triggerLabel}</Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto bg-offwhite">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-indigo-deep">Enquire — {entity.name}</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-4">
          <Field label="Full name"><Input data-testid="inquiry-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Email"><Input data-testid="inquiry-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label="Phone"><Input data-testid="inquiry-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Travel date"><Input data-testid="inquiry-date" type="date" value={f.travel_date} onChange={(e) => setF({ ...f, travel_date: e.target.value })} /></Field>
            <Field label="Travelers"><Input data-testid="inquiry-travelers" type="number" min="1" value={f.travelers} onChange={(e) => setF({ ...f, travelers: Number(e.target.value) })} /></Field>
          </div>
          <Field label="Message"><Textarea data-testid="inquiry-message" rows={3} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Any special requirements?" /></Field>
          <Button data-testid="inquiry-submit" onClick={submit} disabled={sending} className="w-full h-11 bg-gold text-indigo-deep hover:bg-gold-dark hover:text-white font-semibold">
            {sending ? "Sending…" : "Send Inquiry"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-indigo-deep/60">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
